import { createPortal } from 'react-dom';
import { useCallback, useLayoutEffect, useRef, useState } from 'react';

const VIEWPORT_MARGIN = 8;

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const AnchoredOverlay = ({
  open,
  anchorRef,
  overlayRef: externalOverlayRef,
  children,
  align = 'end',
  preferredPlacement = 'bottom',
  offset = 8,
  minWidth,
  matchWidth = false,
  zIndex = 80,
  className = '',
  onPositioned,
}) => {
  const internalOverlayRef = useRef(null);
  const overlayRef = externalOverlayRef || internalOverlayRef;
  const [style, setStyle] = useState(null);
  const [placement, setPlacement] = useState(null);
  const lockedPlacementRef = useRef(null);

  const updatePosition = useCallback(() => {
    if (!open || !anchorRef?.current || !overlayRef.current) return;

    const anchorRect = anchorRef.current.getBoundingClientRect();
    const overlayRect = overlayRef.current.getBoundingClientRect();

    const width = Math.max(
      matchWidth ? anchorRect.width : overlayRect.width,
      minWidth || 0
    );
    const height = overlayRect.height;

    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    const canFitBelow = anchorRect.bottom + offset + height <= viewportHeight - VIEWPORT_MARGIN;
    const canFitAbove = anchorRect.top - offset - height >= VIEWPORT_MARGIN;
    const canFitRight = anchorRect.right + offset + width <= viewportWidth - VIEWPORT_MARGIN;
    const canFitLeft = anchorRect.left - offset - width >= VIEWPORT_MARGIN;

    let initialPlacement = preferredPlacement;
    if (preferredPlacement === 'bottom') {
      initialPlacement = !canFitBelow && canFitAbove ? 'top' : 'bottom';
    } else if (preferredPlacement === 'top') {
      initialPlacement = canFitAbove || !canFitBelow ? 'top' : 'bottom';
    } else if (preferredPlacement === 'right') {
      initialPlacement = !canFitRight && canFitLeft ? 'left' : 'right';
    } else if (preferredPlacement === 'left') {
      initialPlacement = canFitLeft || !canFitRight ? 'left' : 'right';
    }

    const resolvedPlacement = lockedPlacementRef.current || initialPlacement;
    lockedPlacementRef.current = resolvedPlacement;
    setPlacement(resolvedPlacement);

    const isHorizontalPlacement = resolvedPlacement === 'top' || resolvedPlacement === 'bottom';

    let left = anchorRect.left;
    let top = anchorRect.bottom + offset;

    if (isHorizontalPlacement) {
      if (align === 'start') {
        left = anchorRect.left;
      } else if (align === 'center') {
        left = anchorRect.left + ((anchorRect.width - width) / 2);
      } else {
        left = anchorRect.right - width;
      }

      top = resolvedPlacement === 'top'
        ? anchorRect.top - offset - height
        : anchorRect.bottom + offset;
    } else {
      if (align === 'start') {
        top = anchorRect.top;
      } else if (align === 'center') {
        top = anchorRect.top + ((anchorRect.height - height) / 2);
      } else {
        top = anchorRect.bottom - height;
      }

      left = resolvedPlacement === 'left'
        ? anchorRect.left - offset - width
        : anchorRect.right + offset;
    }

    setStyle({
      position: 'fixed',
      top: `${clamp(top, VIEWPORT_MARGIN, viewportHeight - height - VIEWPORT_MARGIN)}px`,
      left: `${clamp(left, VIEWPORT_MARGIN, viewportWidth - width - VIEWPORT_MARGIN)}px`,
      width: `${width}px`,
      zIndex,
      visibility: 'visible',
    });
  }, [align, anchorRef, matchWidth, minWidth, offset, open, overlayRef, preferredPlacement, zIndex]);

  useLayoutEffect(() => {
    if (!open) {
      setStyle(null);
      setPlacement(null);
      lockedPlacementRef.current = null;
      return undefined;
    }

    const frame = requestAnimationFrame(updatePosition);
    const handleWindowChange = () => updatePosition();

    window.addEventListener('resize', handleWindowChange);
    window.addEventListener('scroll', handleWindowChange, true);

    let resizeObserver = null;
    if (typeof ResizeObserver !== 'undefined' && overlayRef.current) {
      resizeObserver = new ResizeObserver(() => updatePosition());
      resizeObserver.observe(overlayRef.current);
    }

    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('resize', handleWindowChange);
      window.removeEventListener('scroll', handleWindowChange, true);
      resizeObserver?.disconnect();
    };
  }, [open, overlayRef, updatePosition]);

  const isPositioned = open && style?.visibility === 'visible';
  useLayoutEffect(() => {
    if (isPositioned) onPositioned?.();
  }, [isPositioned, onPositioned]);

  if (!open) return null;

  return createPortal(
    <div
      ref={overlayRef}
      data-placement={placement || preferredPlacement}
      style={style || {
        position: 'fixed',
        top: '-9999px',
        left: '-9999px',
        visibility: 'hidden',
        zIndex,
      }}
      className={className}
    >
      {children}
    </div>,
    document.body
  );
};

export default AnchoredOverlay;
